// The version selector's pure model (design **T3**, design doc §3.3/§3.3.2): the sandbox's
// document history crossed with the runs it has produced, reduced to menu rows.
//
// The one structural decision this file exists to hold is the one the architecture review
// (**A2**) forced: **a row's identity is the candidate — the run — while its presentation is
// version-first.** A version and a run are not one-to-one. Pressing Run twice on an unchanged
// document produces two runs today, and `rounds-candidate` will make two runs of the same bytes
// distinguishable by run configuration, each with its own score and its own trace. A menu keyed
// on the version would therefore silently drop candidates and leave a future selection surface
// nothing to attach to. So one row is emitted per version, showing that version's most recent
// run and taking that run's identity, and every other run of that version stays addressable
// underneath it — a run count that expands rather than a list that truncates.
//
// The second rule is honesty (**Goal 3**): every figure here is sourced or explicitly absent. A
// version that has never been simulated reports {@link NOT_RUN_LABEL}, not a zero — because a run
// that genuinely scored zero is a different fact, and the two must not render the same. A run
// that produced no score says what it is doing instead.
//
// What this file deliberately cannot do is submit or select a candidate for grading. That belongs
// to `rounds-client`, and its absence here is structural rather than conventional: a row is inert
// data with no invokable field, so there is no handler a component could wire a grading
// submission to by accident.
//
// No React, no DOM, no fetch — the whole selector's behaviour is decidable from plain data.

import type { RunStatus } from '../../net/rounds-http';

// --- inputs ------------------------------------------------------------------------

/**
 * One entry of `GET /rounds/{sandbox}/versions` (the settled version-surface contract §1).
 *
 * Declared here because the network types do not exist yet; when the route lands, this moves to
 * `client/src/net/rounds-http.ts` beside `RunDto` and this file imports it from there.
 *
 * `text` is deliberately absent from the wire — a history of whole documents is unbounded, and
 * the text arrives with activation, which is the only moment it is needed. Nothing in this model
 * wants it.
 */
export interface VersionSummary {
  /** 1-based, ascending. The wire sends the list ascending; the display reverses it. */
  ordinal: number;
  /** The one-line description of what this version changed. */
  summary: string;
  /** Where the version came from. Never inferred from the summary text. */
  origin: 'agent' | 'manual';
  /** Whether the version's document compiles. */
  validates: boolean;
  /**
   * The two-or-three-word name the agent chose from the player's prompt, or `null`/absent when it
   * has none. A name is what lets a player tell their attempts apart at a glance; its absence falls
   * back to {@link MenuRowBase.ordinalLabel}, which is the stable never-reused ordinal.
   */
  name?: string | null;
  /**
   * The player prompt that produced this version, **denormalised onto the version row** at commit.
   *
   * This is why the menu no longer needs a message map: the prompt travels with the version it
   * caused, so a just-committed version's subtitle is the prompt rather than a placeholder waiting
   * for the next thread fetch.
   */
  prompt?: string | null;
  /** Present only when `origin === 'agent'` — the chat message that caused the version. */
  threadId?: string;
  messageId?: string;
}

/**
 * One run, as this model reads it: the subset of `rounds-http`'s `RunDto` that decides a row,
 * plus the `documentVersion` field the version-surface contract §3 adds to it.
 *
 * `documentVersion` is `null` for any run recorded before the field existed. Such a run is
 * **unattributed** — see {@link VersionMenu.unattributedRuns}.
 */
export interface MenuCandidate {
  runId: number;
  /** The document ordinal the run was submitted from; `null` when the surface never recorded one. */
  documentVersion: number | null;
  status: RunStatus;
  /** The G1 score, or `null` while the run has not produced one. Never coerced to a number. */
  score: number | null;
}

/** The uncommitted working document, when the editor holds edits that are not yet a version. */
export interface DraftState {
  /** How many diagnostics the working document currently carries; `0` when it compiles. */
  errorCount: number;
}

export interface VersionMenuInput {
  /** The ordinal the working document is based on — the version the editor is currently on. */
  headOrdinal: number;
  /** The history, in the ascending order the route sends. */
  versions: readonly VersionSummary[];
  /** Every run the sandbox has recorded, in any order. */
  runs: readonly MenuCandidate[];
  /** Present only while the working document has uncommitted edits. */
  draft?: DraftState | null;
  /**
   * A run is in flight for {@link headOrdinal} right now — the screen's own coordinator state
   * (`SandboxState.run.kind === 'in-flight'`), not anything read off `runs`.
   *
   * It has to be threaded in separately because `runs` genuinely lags the press: a tournament run
   * is not in that list until the server records it, and a practice run is not in it until the
   * device's history is refetched — both of which settle *after* Run is pressed, never before. Left
   * unheld, the trigger read the stale fact underneath: `not run` for the very version now being
   * simulated, or — on a fresh sandbox whose only version is the implicit starting one, never
   * itself listed by the versions route — `No versions` outright, while a run for it was plainly in
   * progress. `headOrdinal` is trusted as the one real fact always in hand (it is the document
   * loaded in the editor), so this flag never has to fabricate anything beyond "there is a version
   * here, and it is running".
   */
  headRunInFlight?: boolean;
  /**
   * The tournament's shared **preview** trace is what is loaded under the playhead right now
   * (`SandboxState.world.isPreview`). Renames the active row's absent-run readout and nothing
   * else — it adds no run, no score and no candidate, because the preview is none of those.
   */
  previewLoaded?: boolean;
  /**
   * Which order the version rows come out in. `'recent'` (the default, and the order the selector
   * has always had) is newest ordinal first; `'score'` is best score first, with versions that have
   * produced no score at all last — so the toggle answers "carry on where I was" or "find my best"
   * without either answer being a different list.
   *
   * The draft always leads, whichever is chosen: it is the document in the editor, not a contender.
   */
  sort?: VersionMenuSort;
}

/** The two orders the selector offers. */
export type VersionMenuSort = 'recent' | 'score';

// --- outputs -----------------------------------------------------------------------

/** The word for a version that has never been simulated. Carries no digit, deliberately: a
 * "0" here would be indistinguishable from a run that genuinely scored nothing. */
export const NOT_RUN_LABEL = 'not run';

/** The draft's equivalent — it has not been *committed*, so "not run" would understate it. */
export const UNSAVED_LABEL = 'unsaved';

/**
 * What the **active** row reads while the tournament's shared preview trace is the thing under the
 * playhead (`rounds-preview-run`).
 *
 * `not run` is still literally true of the version — the preview is nobody's run and is counted as
 * nobody's candidate — but printed over a visibly playing 26-minute trace it read as a
 * contradiction at the one beat where every player in the room looks at this control at once (E2E
 * round 3). Naming what is actually on screen costs no accuracy: the row still carries no run and
 * no score, and pressing Run still runs this version.
 */
export const PREVIEW_LABEL = 'tournament preview';

/** The word for the version a run is in flight for right now — see
 * {@link VersionMenuInput.headRunInFlight}. Carries the ellipsis `not run`/`unsaved` do not: this
 * one names something ongoing, not a settled absence. */
export const RUNNING_LABEL = 'running…';

/** The draft row's summary line. The version route supplies a summary per committed version; an
 * uncommitted document has none, and inventing one from the diff would be a fabricated value. */
export const DRAFT_SUMMARY = 'Uncommitted edits';

/**
 * A row's score readout: either a score, or an explicit statement of why there is none.
 *
 * `unscored` carries no `score` field at all rather than a `null` one, so a renderer cannot
 * accidentally format the absence as a number.
 */
export type RunOutcome =
  | { kind: 'not-run'; label: string }
  /** A run is currently in flight for this row's version — {@link VersionMenuInput.headRunInFlight}
   * asserted so, and nothing else can: it carries no `runId` because the run may not exist in
   * `runs` yet at all (see that field's own note). */
  | { kind: 'running'; label: string }
  | { kind: 'scored'; runId: number; score: number; label: string }
  | { kind: 'unscored'; runId: number; label: string };

/**
 * One candidate under a row. **This is the addressable unit** — a version run more than once has
 * one of these per run, each with its own identity and its own score, so nothing a Run produced
 * is lost to the version-first presentation.
 */
export interface MenuRun {
  /** Stable identity, candidate-scoped. */
  id: string;
  runId: number;
  status: RunStatus;
  score: number | null;
  /** The score as it reads on screen, or what the run is doing instead. Never `'0'` for a run
   * that produced no score. */
  scoreLabel: string;
}

interface MenuRowBase {
  /**
   * The row's identity. **Candidate-scoped** (`run:{runId}`) whenever the row has a run, because
   * the candidate is what the row actually stands for; version-scoped (`version:{ordinal}`) only
   * when there is no candidate to borrow an identity from.
   */
  id: string;
  /**
   * `#4` — the **stable, never-reused** per-sandbox ordinal, for the draft the one Run would cut,
   * one past the head. It is the row's identity when the agent chose no name for the version, which
   * is why it is a number a player can remember rather than a position in a list.
   */
  ordinalLabel: string;
  /** The row's headline: the agent-chosen name where there is one, the version's own summary
   * otherwise. */
  summary: string;
  /** The player prompt that produced this version, or `null` — rendered as the row's subtitle. */
  prompt: string | null;
  provenance: 'agent' | 'manual';
  /** `Agent` / `Manual`, or `Draft · 2 errors` on a draft that does not compile. */
  provenanceLabel: string;
  outcome: RunOutcome;
  /** Every run of this version, newest first. Empty when it has never been simulated. */
  runs: readonly MenuRun[];
  /** `runs.length`. Greater than 1 means the row expands to its runs. */
  runCount: number;
  /** Whether this row is the document currently in the editor. */
  isActive: boolean;
}

export interface DraftMenuRow extends MenuRowBase {
  kind: 'draft';
  /** `0` when the working document compiles. */
  errorCount: number;
}

export interface VersionMenuRow extends MenuRowBase {
  kind: 'version';
  ordinal: number;
  /** Present only on an agent-origin row — the message that caused the version. */
  agentMessage?: { threadId: string; messageId: string };
}

export type MenuRow = DraftMenuRow | VersionMenuRow;

export interface VersionMenu {
  /** Newest-first for display: the draft (when present), then versions descending. */
  rows: readonly MenuRow[];
  /**
   * Runs the surface could not attribute to a version — `documentVersion: null`, or an ordinal
   * absent from the history. They are grouped under **no** version rather than being folded into
   * version 0, which would assert a provenance nothing recorded; and they are surfaced here
   * rather than discarded, so a run is never silently lost.
   */
  unattributedRuns: readonly MenuRun[];
}

// --- the model ---------------------------------------------------------------------

/** What a run without a score is doing, said plainly. */
function statusLabel(status: RunStatus): string {
  switch (status) {
    case 'in_flight':
      return 'running';
    case 'cancelled':
      return 'cancelled';
    case 'completed':
      // Terminal with nothing to show: honest rather than a zero.
      return 'no score';
    default:
      // `simulation_failed`, `trace_io_failed`, and any status a newer server adds. The player's
      // question is the same in every case — it did not produce a score.
      return 'failed';
  }
}

function menuRunFrom(candidate: MenuCandidate): MenuRun {
  return {
    id: `run:${candidate.runId}`,
    runId: candidate.runId,
    status: candidate.status,
    score: candidate.score,
    scoreLabel:
      candidate.score === null ? statusLabel(candidate.status) : String(candidate.score),
  };
}

/** The row-level readout for a version's most recent run, or its absence. */
function outcomeFrom(latest: MenuRun | undefined, absentLabel: string): RunOutcome {
  if (latest === undefined) {
    return { kind: 'not-run', label: absentLabel };
  }
  if (latest.score === null) {
    return { kind: 'unscored', runId: latest.runId, label: latest.scoreLabel };
  }
  return { kind: 'scored', runId: latest.runId, score: latest.score, label: latest.scoreLabel };
}

/**
 * Summaries this bare are not real descriptions of what changed — they read as placeholder text
 * a player can't act on. Recognized case-insensitively (trimmed) so a stray capitalization or
 * empty string doesn't slip through.
 */
const UNHELPFUL_SUMMARIES = new Set(['', 'run']);

/**
 * The version's headline text, with an unhelpful stored summary replaced by something a player
 * can actually read. Version 1 is always the starting roster, so it gets its own honest label
 * rather than a generic one; every later version falls back by provenance — a hand edit says so
 * plainly, and an agent-authored one keeps its own (already-informative, in the ordinary case)
 * summary rather than being handed a fabricated one.
 */
function displaySummary(version: VersionSummary): string {
  // **The agent's chosen name wins.** It is two or three words picked from the player's own prompt,
  // which is the thing a player recognises; the stored summary is a description of the change and is
  // the fallback, not the headline.
  const name = version.name?.trim();
  if (name !== undefined && name !== '') {
    return name;
  }
  if (!UNHELPFUL_SUMMARIES.has(version.summary.trim().toLowerCase())) {
    return version.summary;
  }
  if (version.ordinal === 1) {
    return 'Starting scripts';
  }
  return version.origin === 'agent' ? version.summary : 'Hand edit';
}

/** `Draft`, or `Draft · 2 errors` when the working document does not compile. */
function draftLabel(errorCount: number): string {
  if (errorCount <= 0) {
    return 'Draft';
  }
  return `Draft · ${errorCount} ${errorCount === 1 ? 'error' : 'errors'}`;
}

/**
 * Versions × candidates → menu rows.
 *
 * Pure and total: it reads its whole input and reaches no clock, no store and no network.
 */
export function buildVersionMenu(input: VersionMenuInput): VersionMenu {
  const known = new Set(input.versions.map((version) => version.ordinal));
  const hasDraft = input.draft != null;

  // **Whether the head will get a synthesized row, decided up front** (adversarial review F3).
  // This used to be computed only after run grouping, down where the row is actually spliced in —
  // but a candidate whose `documentVersion` is exactly the (unlisted) head ordinal is exactly as
  // attributable as one whose ordinal is listed, and classifying it by `known` alone stranded it
  // in `unattributedRuns` before this decision ever ran. The synthesized row then hardcoded
  // `runs: []` / `not-run` over a run that was sitting right there — so a failed or stale
  // `versions` response paired with a successful `runs` response read a false "not run" on the
  // active row while the real run went unsurfaced. See the identical condition where the row is
  // built, further down — this is the one place it is computed.
  const nameHead =
    !hasDraft &&
    !known.has(input.headOrdinal) &&
    (input.headOrdinal >= 1 || input.headRunInFlight === true);
  const attributable = nameHead ? new Set([...known, input.headOrdinal]) : known;

  // Newest run first everywhere. `runId` is server-global and increasing, so it is the ordering
  // the surface itself defines — no wall-clock is consulted to decide "most recent".
  const byVersion = new Map<number, MenuRun[]>();
  const unattributedRuns: MenuRun[] = [];
  for (const candidate of [...input.runs].sort((a, b) => b.runId - a.runId)) {
    const entry = menuRunFrom(candidate);
    const ordinal = candidate.documentVersion;
    if (ordinal === null || !attributable.has(ordinal)) {
      unattributedRuns.push(entry);
      continue;
    }
    const bucket = byVersion.get(ordinal);
    if (bucket === undefined) {
      byVersion.set(ordinal, [entry]);
    } else {
      bucket.push(entry);
    }
  }

  const rows: MenuRow[] = [];

  if (input.draft != null) {
    rows.push({
      kind: 'draft',
      id: 'draft',
      ordinalLabel: `#${input.headOrdinal + 1}`,
      summary: DRAFT_SUMMARY,
      prompt: null,
      provenance: 'manual',
      provenanceLabel: draftLabel(input.draft.errorCount),
      errorCount: input.draft.errorCount,
      outcome: { kind: 'not-run', label: UNSAVED_LABEL },
      runs: [],
      runCount: 0,
      // The draft *is* the working document, so while one exists no committed version is.
      isActive: true,
    });
  }

  for (const version of [...input.versions].sort((a, b) => b.ordinal - a.ordinal)) {
    const runs = byVersion.get(version.ordinal) ?? [];
    const latest = runs[0];
    rows.push({
      kind: 'version',
      id: latest === undefined ? `version:${version.ordinal}` : latest.id,
      ordinal: version.ordinal,
      ordinalLabel: `#${version.ordinal}`,
      summary: displaySummary(version),
      prompt: version.prompt ?? null,
      provenance: version.origin,
      provenanceLabel: version.origin === 'agent' ? 'Agent' : 'Manual',
      ...(version.origin === 'agent' && version.threadId != null && version.messageId != null
        ? { agentMessage: { threadId: version.threadId, messageId: version.messageId } }
        : {}),
      outcome: outcomeFrom(latest, NOT_RUN_LABEL),
      runs,
      runCount: runs.length,
      isActive: !hasDraft && version.ordinal === input.headOrdinal,
    });
  }

  // **The head is always a row, even when the history has not listed it** (E2E round-2 finding
  // **N-7**, 2026-08-09).
  //
  // `headOrdinal` and `versions` are two separate reads at every moment, and only the first moves
  // with a save: the ordinal arrives in the save's own response and is applied synchronously, while
  // `GET /versions` is a second request issued afterwards — one that `RoundsHttpClient.listVersions`
  // answers as the empty history for a refusal, a garbled body and a dead transport alike. Left to
  // `versions` alone, a menu holding a perfectly well-known head found no row to mark active and the
  // trigger read `No versions` over the document the player had just saved. Nothing re-issued the
  // read either, because the refresh is keyed on the head *moving*, which it already had — so in the
  // tournament sandbox that reading was permanent for the rest of the editing window.
  //
  // This is the same synthesis the in-flight override used to perform on its own, lifted out from
  // under `headRunInFlight`: that flag was the evidence in hand when the practice scope was
  // repaired, not the condition the repair depended on. Both overrides below now find this row and
  // rename its outcome, so a run in flight and a loaded preview keep saying exactly what they said.
  //
  // Nothing is invented beyond the ordinal itself, which is trusted per {@link
  // VersionMenuInput.headRunInFlight}'s note. **`0` is the exception in one direction only**: it is
  // the "no known base" sentinel no sandbox ever issues (`documentSnapshotOf`), so at rest there is
  // genuinely no version to name and a `v0` reading `not run` would be a claim about a document
  // that does not exist. While a run is in flight for it there *is* something true to say — the
  // thing the player pressed Run on is running — and that is the practice repair's original case,
  // kept exactly as it was rather than lost to the generalisation.
  //
  // `nameHead` itself is computed once, up top, precisely so run attribution can agree with this
  // decision before it runs (**F3**) — see that computation's own comment.
  if (nameHead) {
    // Populated from `byVersion`, not hardcoded to "no run": the loop above already attributes
    // any candidate naming this exact (unlisted) ordinal here, via `attributable`.
    const headRuns = byVersion.get(input.headOrdinal) ?? [];
    const latest = headRuns[0];
    const synthesized: VersionMenuRow = {
      kind: 'version',
      // Candidate-scoped whenever a run is attributed to it, exactly like every other row — see
      // `MenuRowBase.id`'s own rule.
      id: latest === undefined ? `version:${input.headOrdinal}` : latest.id,
      ordinal: input.headOrdinal,
      ordinalLabel: `#${input.headOrdinal}`,
      // Blank rather than guessed: the summary is the versions route's to supply, and a row that
      // route has not served has none. `outcomeFrom` still reports a real attributed run truthfully.
      summary: '',
      prompt: null,
      provenance: 'manual',
      provenanceLabel: '',
      outcome: outcomeFrom(latest, NOT_RUN_LABEL),
      runs: headRuns,
      runCount: headRuns.length,
      isActive: true,
    };
    // Descending, like every other version row: an unlisted head is newer than the listed ones.
    const below = rows.findIndex(
      (row) => row.kind === 'version' && row.ordinal < input.headOrdinal,
    );
    rows.splice(below === -1 ? rows.length : below, 0, synthesized);
  }

  // **The preview override.** Only the active row, only while it has no run of its own to report,
  // and only the *word* — the row keeps its empty `runs`, so nothing downstream can mistake the
  // preview for a candidate. The in-flight override below deliberately runs after this one and
  // wins: once the player presses Run, what they pressed is the thing to name.
  if (input.previewLoaded === true && !hasDraft) {
    const headRow = rows.find(
      (row): row is VersionMenuRow => row.kind === 'version' && row.ordinal === input.headOrdinal,
    );
    if (headRow?.outcome.kind === 'not-run') {
      headRow.outcome = { kind: 'not-run', label: PREVIEW_LABEL };
    }
  }

  // **The in-flight override** — see `headRunInFlight`'s own note for why `runs` cannot be trusted
  // to carry this yet. Applied after every row is built, so it always wins over whatever `runs`
  // said about the head version, including a *stale* scored run from before this one was pressed.
  //
  // It no longer has a synthesis arm of its own: the block above already guarantees a head row
  // exists whenever one can honestly be drawn, this flag included, so this only ever has to rename
  // one.
  if (input.headRunInFlight === true && !hasDraft) {
    const headRow = rows.find(
      (row): row is VersionMenuRow => row.kind === 'version' && row.ordinal === input.headOrdinal,
    );
    if (headRow !== undefined) {
      headRow.outcome = { kind: 'running', label: RUNNING_LABEL };
    }
  }

  // **Sorted last, after every override has found its row.** The overrides above locate the head by
  // ordinal rather than by position, so reordering here cannot disturb them — and doing it last is
  // what keeps the two orders one list in two orders rather than two lists.
  //
  // The draft is pinned to the front in both, because it is the document in the editor: it has no
  // score to rank by and demoting it under scored contenders would hide the thing being edited.
  if ((input.sort ?? 'recent') === 'score') {
    const draftRows = rows.filter((row) => row.kind === 'draft');
    const versionRows = rows
      .filter((row): row is VersionMenuRow => row.kind === 'version')
      .sort((a, b) => {
        const left = bestScore(a);
        const right = bestScore(b);
        if (left === right) {
          // A tie — including two versions that have never scored — falls back to the recency order,
          // so the toggle never produces an arbitrary shuffle within a band.
          return b.ordinal - a.ordinal;
        }
        // Unscored last, whichever way round: a version with no score is not "score 0".
        if (left === null) {
          return 1;
        }
        if (right === null) {
          return -1;
        }
        return right - left;
      });
    return { rows: [...draftRows, ...versionRows], unattributedRuns };
  }

  return { rows, unattributedRuns };
}

/** The best score any run of this row produced, or `null` when none produced one. */
function bestScore(row: MenuRow): number | null {
  let best: number | null = null;
  for (const run of row.runs) {
    if (run.score !== null && (best === null || run.score > best)) {
      best = run.score;
    }
  }
  return best;
}
